import { License } from "../../store/types";
import { ColumnMeta, CustomAction } from "../CustomDatatable/CustomDatatable";
import { formatPeriod, formatDate } from "../../hooks/dateTimeConvertion";
import StatusTag from "../StatusTag/StatusTag";
import { MenuItem } from "primereact/menuitem";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import DateFilterMenu from "./DateFilterMenu";
import {
  IconPlusBlack,
  IconCircleCheck,
  IconCircleX,
} from "../../assets/icons/Icon";

export const getLicenseContextMenuItems = (
  selectedLicense: License | null,
  handleAddUser: (license: License) => void,
  handleEnable: (license: License) => void,
  handleDisable: (license: License) => void
): MenuItem[] => {
  if (!selectedLicense) return [];

  const items: MenuItem[] = [];

  let diffMs = -Infinity;
  if (selectedLicense.expirationDate) {
    const end = new Date(selectedLicense.expirationDate);
    const now = new Date();
    if (!isNaN(end.getTime())) {
      diffMs = end.getTime() - now.getTime();
    }
  }

  if (!selectedLicense.userId && selectedLicense.type !== "session") {
    items.push({
      label: "Добавить пользователя",
      icon: <IconPlusBlack size={14} />,
      command: () => handleAddUser(selectedLicense),
    });
  }

  if (selectedLicense.isEnabled) {
    items.push({
      label: "Деактивировать",
      icon: <IconCircleX size={14} />,
      command: () => handleDisable(selectedLicense),
    });
  } else if (!selectedLicense.isEnabled && diffMs > 0) {
    items.push({
      label: "Активировать",
      icon: <IconCircleCheck size={14} />,
      command: () => handleEnable(selectedLicense),
    });
  }

  return items;
};

export const getLicenseMobileActions = (
  handleEnable: (license: License) => void,
  handleDisable: (license: License) => void
): CustomAction<License>[] => {
  return [
    {
      icon: <IconCircleCheck size={16} />,
      onClick: (license: License) => handleEnable(license),
      condition: (license: License) => {
        if (license.isEnabled) return false;
        if (!license.expirationDate) return true;
        const end = new Date(license.expirationDate);
        const now = new Date();
        return end > now;
      },
    },
    {
      icon: <IconCircleX size={16} />,
      onClick: (license: License) => handleDisable(license),
      condition: (license: License) => license.isEnabled,
    },
  ];
};

export const isActivatedBodyTemplate = (rowData: License) => {
  const status = rowData.isEnabled ? "success" : "danger";
  const label = rowData.isEnabled ? "Активирована" : "Деактивирована";
  return <StatusTag status={status} label={label} />;
};

export const validityPeriodTemplate = (rowData: License) => {
  if (!rowData.startDate || !rowData.expirationDate) return "—";
  const start = new Date(rowData.startDate);
  const end = new Date(rowData.expirationDate);
  const diffMs = end.getTime() - start.getTime();
  return formatPeriod(diffMs);
};

export const timeRemainingTemplate = (rowData: License) => {
  if (!rowData.expirationDate) return "—";

  const expiration = new Date(rowData.expirationDate);
  const now = new Date();

  const endDate = new Date(
    expiration.getFullYear(),
    expiration.getMonth(),
    expiration.getDate()
  );
  const currentDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );

  const diffMs = endDate.getTime() - currentDate.getTime();

  if (diffMs < 0) {
    return <StatusTag status="danger" label="Истекло" />;
  }

  const daysRemaining = diffMs / (1000 * 60 * 60 * 24);
  const status = daysRemaining >= 30 ? "success" : "warning";
  const label = formatPeriod(diffMs);

  return <StatusTag status={status} label={label} />;
};

export const dateTemplate = (field: keyof License) => (rowData: License) => {
  const value = rowData[field] as string;
  return value ? formatDate(value) : "—";
};

const textFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
  return (
    <InputText
      type="text"
      value={options.value || ""}
      onChange={(e) => options.filterCallback(e.target.value)}
      placeholder="Поиск..."
      className="p-column-filter"
    />
  );
};

const typeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
  return (
    <MultiSelect
      value={options.value}
      options={[
        { label: "Временная", value: "Временная" },
        { label: "Бессрочная", value: "Бессрочная" },
        { label: "Сессионная", value: "Сессионная" },
      ]}
      onChange={(e: MultiSelectChangeEvent) => options.filterCallback(e.value)}
      optionLabel="label"
      placeholder="Тип"
      className="p-column-filter"
    />
  );
};

const isEnabledFilterTemplate = (
  options: ColumnFilterElementTemplateOptions
) => {
  return (
    <MultiSelect
      value={options.value}
      options={[
        { label: "Активирована", value: true },
        { label: "Деактивирована", value: false },
      ]}
      onChange={(e: MultiSelectChangeEvent) => options.filterCallback(e.value)}
      optionLabel="label"
      placeholder="Любая"
      className="p-column-filter"
    />
  );
};

const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
  return (
    <DateFilterMenu
      value={options.value}
      filterCallback={options.filterCallback}
    />
  );
};

export type ValidityPeriodFilter =
  | null
  | "less2weeks"
  | "less1month"
  | "less6months"
  | "less1year"
  | "1to3years"
  | "more3years";

export type TimeRemainingFilter =
  | null
  | "expired"
  | "less30days"
  | "more30days"
  | "less1year"
  | "more1year";

export const validityPeriodOptions = [
  { label: "Любой", value: null },
  { label: "До 2 недель", value: "less2weeks" },
  { label: "До 1 месяца", value: "less1month" },
  { label: "До 6 месяцев", value: "less6months" },
  { label: "До 1 года", value: "less1year" },
  { label: "1–3 года", value: "1to3years" },
  { label: "Более 3 лет", value: "more3years" },
];

export const timeRemainingOptions = [
  { label: "Любое", value: null },
  { label: "Истекло", value: "expired" },
  { label: "Менее 30 дней", value: "less30days" },
  { label: "30 дней и более", value: "more30days" },
  { label: "До 1 года", value: "less1year" },
  { label: "Более 1 года", value: "more1year" },
];

const validityPeriodFilterTemplate = (
  options: ColumnFilterElementTemplateOptions
) => {
  return (
    <Dropdown
      value={options.value}
      options={validityPeriodOptions}
      onChange={(e) => options.filterCallback(e.value)}
      placeholder="Срок действия"
      className="p-column-filter"
    />
  );
};

const timeRemainingFilterTemplate = (
  options: ColumnFilterElementTemplateOptions
) => {
  return (
    <Dropdown
      value={options.value}
      options={timeRemainingOptions}
      onChange={(e) => options.filterCallback(e.value)}
      placeholder="Оставшееся время"
      className="p-column-filter"
    />
  );
};

export const filterByValidityPeriod = (value: number, filter: string) => {
  if (!filter) return true;
  const days = value ?? 0;
  switch (filter) {
    case "less2weeks":
      return days < 14;
    case "less1month":
      return days < 30;
    case "less6months":
      return days < 180;
    case "less1year":
      return days < 365;
    case "1to3years":
      return days >= 365 && days <= 1095;
    case "more3years":
      return days > 1095;
    default:
      return true;
  }
};

export const filterByTimeRemaining = (value: number, filter: string | null) => {
  if (!filter) return true;
  const days = value ?? 0;
  switch (filter) {
    case "expired":
      return days < 0;
    case "less30days":
      return days >= 0 && days < 30;
    case "more30days":
      return days >= 30;
    case "less1year":
      return days >= 0 && days < 365;
    case "more1year":
      return days >= 365;
    default:
      return true;
  }
};

const filterTextInput = (value: string, filter: string): boolean => {
  if (!filter) return true;
  if (!value) return false;
  return value.toLowerCase().includes(filter.toLowerCase());
};

const filterLicenseType = (value: string, filter: string[]): boolean => {
  if (!filter || filter.length === 0) return true;
  return filter.includes(value);
};

const filterIsEnabled = (value: boolean, filter: boolean[]): boolean => {
  if (!filter || filter.length === 0) return true;
  return filter.includes(value);
};

export const dateFilterFunction = (
  value: string | Date,
  filter: any
): boolean => {
  if (!filter || !filter.value) return true;
  const rowDate = new Date(value).setHours(0, 0, 0, 0);
  const filterDate = new Date(filter.value).setHours(0, 0, 0, 0);
  switch (filter.operator) {
    case "equals":
      return rowDate === filterDate;
    case "after":
      return rowDate > filterDate;
    case "before":
      return rowDate < filterDate;
    default:
      return true;
  }
};

const projectName = {
  field: "projectName",
  header: "Программное обеспечение",
  filter: true,
  filterField: "projectName",
  filterElement: textFilterTemplate,
  filterFunction: filterTextInput,
  showFilterMatchModes: true,
};

const licenseType = {
  field: "licenseType",
  header: "Тип",
  filter: true,
  filterField: "licenseType",
  filterElement: typeFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterLicenseType,
  showFilterMatchModes: false,
};

const isEnabled = {
  field: "isEnabled",
  header: "Активирована",
  body: isActivatedBodyTemplate,
  filter: true,
  filterField: "isEnabled",
  filterElement: isEnabledFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterIsEnabled,
  showFilterMatchModes: false,
};

const validityPeriod = {
  field: "_validityDays",
  header: "Срок действия",
  body: validityPeriodTemplate,
  filter: true,
  filterField: "_validityDays",
  filterElement: validityPeriodFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterByValidityPeriod,
  showFilterMatchModes: false,
};

const issueDate = {
  field: "issueDate",
  header: "Дата создания",
  body: dateTemplate("issueDate"),
  dataType: "date",
  filter: true,
  filterField: "issueDate",
  filterElement: dateFilterTemplate,
  filterFunction: dateFilterFunction,
  filterMatchMode: "custom",
  showFilterMatchModes: false,
};

const startDate = {
  field: "startDate",
  header: "Дата начала",
  body: dateTemplate("startDate"),
  dataType: "date",
  filter: true,
  filterField: "startDate",
  filterElement: dateFilterTemplate,
  filterFunction: dateFilterFunction,
  filterMatchMode: "custom",
  showFilterMatchModes: false,
};

const expirationDate = {
  field: "expirationDate",
  header: "Дата заверешния",
  body: dateTemplate("expirationDate"),
  dataType: "date",
  filter: true,
  filterField: "expirationDate",
  filterElement: dateFilterTemplate,
  filterFunction: dateFilterFunction,
  filterMatchMode: "custom",
  showFilterMatchModes: false,
};

const timeRemainingDays = {
  field: "_timeRemainingDays",
  header: "Оставшееся время",
  body: timeRemainingTemplate,
  filter: true,
  filterField: "_timeRemainingDays",
  filterElement: timeRemainingFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterByTimeRemaining,
  showFilterMatchModes: false,
};

const lastUse = {
  field: "lastUse",
  header: "Последнее использование",
  body: dateTemplate("lastUse"),
  dataType: "date",
  filter: true,
  filteredField: "lastUse",
  filterElement: dateFilterTemplate,
  filterFunction: dateFilterFunction,
  filterMatchMode: "custom",
  showFilterMatchModes: false,
};

const organizationName = {
  field: "organizationName",
  header: "Компания",
  filter: true,
  filterField: "organizationName",
  filterElement: textFilterTemplate,
  filterFunction: filterTextInput,
  showFilterMatchModes: true,
};

const userName = {
  field: "userName",
  header: "Пользователь",
  filter: true,
  filterField: "userName",
  filterElement: textFilterTemplate,
  filterFunction: filterTextInput,
  showFilterMatchModes: true,
};

export const userLicenseColumns: ColumnMeta<License>[] = [
  projectName,
  licenseType,
  isEnabled,
  validityPeriod,
  startDate,
  expirationDate,
  timeRemainingDays,
  lastUse,
];

export const companyLicenseColumns: ColumnMeta<License>[] = [
  projectName,
  userName,
  licenseType,
  isEnabled,
  validityPeriod,
  startDate,
  expirationDate,
  timeRemainingDays,
  lastUse,
];

export const projectLicenseColumns: ColumnMeta<License>[] = [
  organizationName,
  userName,
  licenseType,
  isEnabled,
  validityPeriod,
  startDate,
  expirationDate,
  timeRemainingDays,
  lastUse,
];

export const extendedLicenseColumns: ColumnMeta<License>[] = [
  projectName,
  organizationName,
  userName,
  licenseType,
  isEnabled,
  issueDate,
  startDate,
  expirationDate,
  validityPeriod,
  timeRemainingDays,
  lastUse,
];
